import { Controller, FieldError, useFormContext } from "react-hook-form";
import { mdiDeleteOutline, mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";

import Select from "components/Select";
import { FR_LINE_TYPE_LIST } from "constants/haulpay-broker";
import { useUserFeature } from "hooks/useUserFeature";
import { IFundingRequest, IFundingRequestForm } from "models/HaulPay";
import styles from "./style.module.scss";
import DollarInput from "components/DollarInput";
import Input from "../../../../../../components/Input";

interface IFRLineItemsProps {
  onChangeLineItemAmount: (amount: number) => void;
}

export default function FRLineItems({ onChangeLineItemAmount }: Readonly<IFRLineItemsProps>) {
  const {
    control,
    formState: { errors },
    getValues,
    watch,
    setValue
  } = useFormContext<IFundingRequestForm | IFundingRequest>();
  const { isAllowLineItems } = useUserFeature();
  const lineItems = watch("lineItems");

  // Custom class for reduced input height
  const shortInputClass =
    "placeholder:text-slate-400 text-slate-700 !text-sm border border-slate-200 !rounded-none !rounded-r-md !px-4 !py-2 !h-10 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow";
  const addLocation = () => {
    setValue("lineItems", [...lineItems, { amount: "" as any, category: FR_LINE_TYPE_LIST[0] }]);
  };

  const removeLocation = (index: number) => {
    setValue(
      "lineItems",
      lineItems.filter((_, i) => i !== index)
    );
  };

  const onChangeAmount = (amount?: string, index?: number) => {
    let totalAmount = 0;
    const items = getValues("lineItems");
    items.forEach((item, i) => {
      if (index !== i) {
        totalAmount += item.amount ? Number(item.amount) : 0;
      }
    });
    onChangeLineItemAmount(totalAmount + Number(amount ?? 0));
  };

  return isAllowLineItems ? (
    <ul className={styles.frLineItemsContainer}>
      {lineItems?.map((item, index) => (
        <li key={index} className="flex flex-col gap-2">
          <div className={styles.list}>
            <DollarInput
              name={`lineItems.${index}.amount`}
              label="&nbsp;Amount"
              error={errors.lineItems?.[index]?.amount as FieldError}
              isHideError
              onChange={(e) => {
                onChangeAmount(e.currentTarget.value, index);
              }}
              className="rounded-l-none flex-1"
              inputClass={shortInputClass}
              prefixUnitClass="!px-3 !py-[0.31rem] flex items-center justify-center font-thin !rounded !rounded-r-none !h-10"
            />
            {item.category.value === "Other" && (
              <Controller
                name={`lineItems.${index}.description`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="&nbsp;Description"
                    error={errors.lineItems?.[index]?.description as FieldError}
                    inputClass={`${shortInputClass} !rounded-l-md`}
                  />
                )}
              />
            )}
            <Controller
              name={`lineItems.${index}.category`}
              control={control}
              render={({ field: { value = item.category, onChange } }) => {
                return (
                  <Select
                    value={value}
                    options={FR_LINE_TYPE_LIST}
                    error={errors.lineItems?.[index]?.category as FieldError}
                    onChange={(newValue) => {
                      onChange(newValue);
                      // Clear description when category is no longer "Other"
                      if (value?.value === "Other" && newValue?.value !== "Other") {
                        setValue(`lineItems.${index}.description`, "");
                      }
                    }}
                    label="&nbsp;Type"
                    className="w-1/3"
                    inputClass={`${shortInputClass} !rounded-l-md`}
                  />
                );
              }}
            />
            <button
              type="button"
              className={styles.deleteButton}
              onClick={() => {
                removeLocation(index);
                onChangeAmount();
              }}>
              <Icon path={mdiDeleteOutline} />
            </button>
          </div>

          <span className="px-2 text-sm text-error">
            {errors.lineItems?.[index]?.amount?.message}
          </span>
        </li>
      ))}

      <div className="flex justify-between">
        <button type="button" onClick={addLocation}>
          <span className="text-info">+ Add Another Charge</span>
        </button>
        <button type="button" onClick={addLocation}>
          <Icon path={mdiPlusCircleOutline} className="size-6" />
        </button>
      </div>
    </ul>
  ) : null;
}
